import {
    GetDebtListByPointIdParams,
    GetPointInfoCollectionParams,
    GetPointInventoryGetParams,
    PatchMarketSetShelfSizeParams,
    PatchPointInventorySetParams,
    PostBuildingChangeGeoParams,
    PostGeoAddParams,
    PostInvoiceMakePaymentByPointParams,
    PostOrderCreateByPointParams,
    PostOrderSaveOneParams,
    PostOrderSaveParams,
    PostTaskAddDebtTaskParams,
    PostTaskAddDistributionPricesParams,
    PostTaskAddRejectTaskParams,
    PostTaskAddSaleTaskParams,
    PostTaskCompleteParams,
} from "tm-shared/api";
import { UserRole } from "tm-shared/types";

import { QueueProgress, QueueTaskStatus } from "@/hooks/core/useAsyncTaskQueue";

export type QueueCommandMap = {
    postOrderCreateByPoint: PostOrderCreateByPointParams;
    postOrderSave: PostOrderSaveParams;
    postOrderSaveOne: PostOrderSaveOneParams;
    postBuildingChangeGeo: PostBuildingChangeGeoParams;
    postTaskAddSaleTaskThunk: PostTaskAddSaleTaskParams;
    getPointInventoryGetThunk: GetPointInventoryGetParams;
    postGeoAdd: PostGeoAddParams;
    postTaskComplete: PostTaskCompleteParams;
    postTaskAddDistributionPricesThunk: PostTaskAddDistributionPricesParams;
    postTaskAddDebtTaskThunk: PostTaskAddDebtTaskParams;
    postInvoiceMakePaymentByPoint: PostInvoiceMakePaymentByPointParams;
    getDebtListByPointIdThunk: GetDebtListByPointIdParams;
    getPointInfoCollectionWorkThunk: GetPointInfoCollectionParams;
    postTaskAddRejectTaskThunk: PostTaskAddRejectTaskParams;
    patchMarketSetShelfSize: PatchMarketSetShelfSizeParams;
    putPointChangePhoto: { photoId: string; buildingId: string };
    patchPointInventorySet: PatchPointInventorySetParams;
    postFileUpload: string;
    updatePhotoStorage: string[];
    deletePhotoFromStorage: string;
    updateCheckout: null;
    getTaskListsThunk: null;
};

export type QueueCommandType = keyof QueueCommandMap;

export const QueueCommandMethods: Partial<Record<QueueCommandType, string>> = {
    postOrderCreateByPoint: "order.createByPoint",
    postOrderSave: "order.save",
    postOrderSaveOne: "order.saveOne",
    postBuildingChangeGeo: "building.changeGeo",
    postTaskAddSaleTaskThunk: "task.addSaleTask",
    getPointInventoryGetThunk: "pointInventory.get",
    postGeoAdd: "geo.add",
    postTaskComplete: "task.complete",
    postTaskAddDistributionPricesThunk: "task.addDistributionPrices",
    postTaskAddDebtTaskThunk: "task.addDebtTask",
    postInvoiceMakePaymentByPoint: "invoice.makePaymentByPoint",
    getDebtListByPointIdThunk: "debt.listByPointId",
    getPointInfoCollectionWorkThunk: "point.infoCollection",
    postTaskAddRejectTaskThunk: "task.addRejectTask",
    patchMarketSetShelfSize: "market.setShelfSize",
    patchPointInventorySet: "pointInventory.set",
};

export type CommandOptions = {
    stopOnError?: boolean;
    showError?: boolean;
    showSuccess?: boolean;
};

export type QueueCommand<T extends QueueCommandType = QueueCommandType> = {
    type: T;
    title: string;
    params: QueueCommandMap[T];
    method: string;
    options?: {
        stopOnError?: boolean;
        ignoreErrorCodes?: string[];
        moveToFailed?: {
            code: string;
            label: string;
        }[];
    };
    resolveOutputs?: {
        // path to the value in the server response, e.g. "result" or "data.id"
        extractPath: string;
        // value that should be replaced in subsequent commands
        replaceValue: string;
    }[];
};

export type CommandParams<T extends QueueCommandType> = QueueCommandMap[T];

export type AnyQueueCommand = {
    [K in QueueCommandType]: QueueCommand<K>;
}[QueueCommandType];

export interface RequestQueueItemMetadata {
    pointId?: string;
    taskId?: string;
    orderId?: string;
    title?: string;
    taskName?: string;
    pointName?: string;
    user?: {
        roleSlug: UserRole;
        userId: string;
    };
}

export interface RequestQueueItem {
    id: string;
    commands: AnyQueueCommand[];
    progress?: QueueProgress;
    metadata?: RequestQueueItemMetadata;
    createdAt: string;
    status: QueueTaskStatus;
    pendingReplacements?: {
        [replaceValue: string]: string; // Record<string, string>
    };
    commandsRevision: number;
    options?: {
        stopOnError?: boolean;
        todayOnly?: boolean;
    };
}
